/**
 * Under the stage: the scrubber, then the transport, the clock, the speed and the tools. The clock
 * is read HERE, in the few leaves that print it, so a playing film re-renders these and not the
 * whole player.
 */

import { Radio } from '@base-ui/react/radio';
import { RadioGroup } from '@base-ui/react/radio-group';
import { Toggle as BaseToggle } from '@base-ui/react/toggle';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { formatClock } from '~/entities/exercise-clip';
import { numberFormat } from '~/shared/lib/intlCache';
import segmented from '~/shared/ui/segmented.module.css';
import {
  CompareGlyph,
  FrameBackGlyph,
  FrameForwardGlyph,
  LoopGlyph,
  MutedGlyph,
  PauseGlyph,
  PlayGlyph,
  SoundGlyph,
  TheatreGlyph,
  ZoomGlyph,
} from '~/shared/ui/VideoGlyphs';

import { useVideoClock, type ClipPlayback } from '../lib/useClipPlayback';
import { PLAYBACK_RATES } from '../model/playback';
import type { PlayerPin } from '../model/playerView';

import styles from './ClipPlayer.module.css';
import { PlayerScrubber } from './PlayerScrubber';

export type PlayerTool = 'compare' | 'zoom' | 'theatre';

interface PlayerControlsProps {
  readonly playback: ClipPlayback;
  readonly pins: readonly PlayerPin[];
  readonly videoUrl: string | null;
  readonly tools: Readonly<Record<PlayerTool, boolean>>;
  readonly onTool: (tool: PlayerTool) => void;
}

export function PlayerControls({ playback, pins, videoUrl, tools, onTool }: PlayerControlsProps) {
  const { t, i18n } = useTranslation();
  const one = numberFormat(i18n.language, { maximumFractionDigits: 2 });
  const clock = (seconds: number) => formatClock(seconds, true, i18n.language);
  const loopLabel =
    playback.loop.from === null
      ? null
      : playback.loop.to === null
        ? t('web:clipPlayer.controls.loopFrom', { from: clock(playback.loop.from) })
        : t('web:clipPlayer.controls.loopRange', {
            from: clock(playback.loop.from),
            to: clock(playback.loop.to),
          });

  return (
    <div className={styles.controls}>
      <PlayerScrubber
        clock={playback.clock}
        duration={playback.duration}
        loop={playback.loop}
        onSeek={playback.seekTo}
        pins={pins}
        videoUrl={videoUrl}
      />
      <div className={styles.controlRow}>
        <div className={styles.controlGroup}>
          <button
            aria-label={t(playback.playing ? 'web:clipPlayer.controls.pause' : 'web:clipPlayer.controls.play')}
            className={styles.icon}
            onClick={playback.togglePlay}
            type="button"
          >
            {playback.playing ? <PauseGlyph className={styles.glyph} /> : <PlayGlyph className={styles.glyph} />}
          </button>
          <button
            aria-label={t('web:clipPlayer.controls.frameBack')}
            className={styles.icon}
            onClick={() => {
              playback.frame(-1);
            }}
            type="button"
          >
            <FrameBackGlyph className={styles.glyphSmall} />
          </button>
          <button
            aria-label={t('web:clipPlayer.controls.frameForward')}
            className={styles.icon}
            onClick={() => {
              playback.frame(1);
            }}
            type="button"
          >
            <FrameForwardGlyph className={styles.glyphSmall} />
          </button>
          <ClockReadout playback={playback} />
          {loopLabel === null ? null : (
            <span className={styles.loopChip}>
              <LoopGlyph className={styles.glyphSmall} />
              {loopLabel}
            </span>
          )}
        </div>
        <div className={styles.controlGroup}>
          <RadioGroup
            aria-label={t('web:clipPlayer.controls.speed')}
            className={`${String(segmented.seg)} ${String(styles.rates)}`}
            onValueChange={(next) => {
              const rate = PLAYBACK_RATES.find((option) => String(option) === next);
              if (rate !== undefined) playback.setRate(rate);
            }}
            value={String(playback.rate)}
          >
            {PLAYBACK_RATES.map((rate) => (
              <Radio.Root
                className={(radio) =>
                  `${String(segmented.segOption)} ${radio.checked ? String(segmented.segOptionOn) : ''}`
                }
                key={rate}
                value={String(rate)}
              >
                {one.format(rate)}×
              </Radio.Root>
            ))}
          </RadioGroup>
          <Toggle
            label={t('web:clipPlayer.controls.loop')}
            on={playback.loop.from !== null}
            onPress={() => {
              if (playback.loop.from === null) playback.loopFrom();
              else if (playback.loop.to === null) playback.loopTo();
              else playback.loopClear();
            }}
          >
            <LoopGlyph className={styles.glyph} />
          </Toggle>
          <Toggle
            label={t('web:clipPlayer.controls.compare')}
            on={tools.compare}
            onPress={() => {
              onTool('compare');
            }}
          >
            <CompareGlyph className={styles.glyph} />
          </Toggle>
          <Toggle
            label={t('web:clipPlayer.controls.zoom')}
            on={tools.zoom}
            onPress={() => {
              onTool('zoom');
            }}
          >
            <ZoomGlyph className={styles.glyph} />
          </Toggle>
          <Toggle
            label={t('web:clipPlayer.controls.theatre')}
            on={tools.theatre}
            onPress={() => {
              onTool('theatre');
            }}
          >
            <TheatreGlyph className={styles.glyph} />
          </Toggle>
          <button
            aria-label={t(playback.muted ? 'web:clipPlayer.controls.unmute' : 'web:clipPlayer.controls.mute')}
            className={styles.icon}
            onClick={playback.toggleMute}
            type="button"
          >
            {playback.muted ? <MutedGlyph className={styles.glyph} /> : <SoundGlyph className={styles.glyph} />}
          </button>
        </div>
      </div>
    </div>
  );
}

function ClockReadout({ playback }: { readonly playback: ClipPlayback }) {
  const { t, i18n } = useTranslation();
  const clock = useVideoClock(playback.clock, (seconds) => formatClock(seconds, true, i18n.language));
  const frame = useVideoClock(playback.clock, (seconds) =>
    Math.round(seconds / playback.frameSeconds()),
  );
  return (
    <>
      <span className={styles.clock}>
        {clock} / {formatClock(playback.duration, false, i18n.language)}
      </span>
      <span className={styles.frame}>{t('web:clipPlayer.controls.frame', { number: frame })}</span>
    </>
  );
}

function Toggle({
  label,
  on,
  onPress,
  children,
}: {
  readonly label: string;
  readonly on: boolean;
  readonly onPress: () => void;
  readonly children: ReactNode;
}) {
  return (
    <BaseToggle
      aria-label={label}
      className={`${String(styles.icon)} ${on ? String(styles.iconOn) : ''}`}
      onPressedChange={onPress}
      pressed={on}
      title={label}
    >
      {children}
    </BaseToggle>
  );
}
